import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext();

function normalizeProfile(profile, fallbackUser = null) {
  if (!profile && !fallbackUser) return null;

  const baseProfile = profile || {};
  const user = fallbackUser || {};
  const id = baseProfile.id || baseProfile.user_id || user.id || null;

  return {
    ...baseProfile,
    ...user,
    id,
    _id: id,
    uid: id,
    email: baseProfile.email || user.email || '',
    displayName: baseProfile.display_name || baseProfile.displayName || user.user_metadata?.display_name || `${baseProfile.first_name || ''} ${baseProfile.last_name || ''}`.trim() || user.email || '',
    firstName: baseProfile.first_name || baseProfile.firstName || user.user_metadata?.first_name || '',
    lastName: baseProfile.last_name || baseProfile.lastName || user.user_metadata?.last_name || '',
    dateOfBirth: baseProfile.date_of_birth || baseProfile.dateOfBirth || user.user_metadata?.date_of_birth || '',
    gender: baseProfile.gender || user.user_metadata?.gender || '',
    country: baseProfile.country || user.user_metadata?.country || '',
    phone: baseProfile.phone || user.user_metadata?.phone || '',
    photoURL: baseProfile.photo_url || baseProfile.photoURL || user.user_metadata?.photo_url || '',
    isAdmin: Boolean(baseProfile.is_admin ?? baseProfile.isAdmin ?? false),
    readOnly: Boolean(baseProfile.read_only ?? baseProfile.readOnly ?? false),
    role: baseProfile.is_admin || baseProfile.isAdmin ? 'admin' : 'user'
  };
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const persistSessionState = useCallback((nextSession, nextProfile = null) => {
    const nextUser = nextSession?.user || null;
    const mergedProfile = normalizeProfile(nextProfile, nextUser);

    setSession(nextSession);
    setUser(nextUser);
    setProfile(mergedProfile);
    setCurrentUser(mergedProfile || nextUser);
  }, []);

  const refreshProfile = useCallback(async (nextSession = null) => {
    if (!supabase) {
      setSession(null);
      setUser(null);
      setProfile(null);
      setCurrentUser(null);
      setLoading(false);
      return null;
    }

    const activeSession = nextSession || (await supabase.auth.getSession()).data.session;
    if (!activeSession?.user) {
      setSession(null);
      setUser(null);
      setProfile(null);
      setCurrentUser(null);
      setLoading(false);
      return null;
    }

    const { data: profileData, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', activeSession.user.id)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    const hydratedProfile = normalizeProfile(profileData, activeSession.user);
    persistSessionState(activeSession, hydratedProfile);
    setLoading(false);
    return hydratedProfile;
  }, [persistSessionState]);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        if (!supabase) {
          setLoading(false);
          return;
        }

        const { data: { session: initialSession } = {} } = await supabase.auth.getSession();
        if (!isMounted) return;
        await refreshProfile(initialSession);
      } catch (error) {
        if (!isMounted) return;
        console.error('Auth initialization error:', error);
        setAuthError(error);
        setCurrentUser(null);
        setLoading(false);
      }
    };

    initialize();

    const { data: subscription } = supabase
      ? supabase.auth.onAuthStateChange(async (_event, nextSession) => {
          if (!isMounted) return;
          try {
            if (!nextSession) {
              setSession(null);
              setUser(null);
              setProfile(null);
              setCurrentUser(null);
              setLoading(false);
              return;
            }
            await refreshProfile(nextSession);
          } catch (error) {
            console.error('Auth state change error:', error);
            setAuthError(error);
          }
        })
      : { data: null };

    return () => {
      isMounted = false;
      subscription?.subscription.unsubscribe();
    };
  }, [refreshProfile]);

  const register = useCallback(async (email, password, displayName, firstName, lastName, dateOfBirth, gender, country, phone) => {
    if (!supabase) {
      throw new Error('Supabase is not configured');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName || `${firstName} ${lastName}`,
          first_name: firstName,
          last_name: lastName,
          date_of_birth: dateOfBirth,
          gender,
          country,
          phone
        }
      }
    });

    if (error) throw error;

    await refreshProfile(data.session);
    return data;
  }, [refreshProfile]);

  const login = useCallback(async (email, password) => {
    if (!supabase) {
      throw new Error('Supabase is not configured');
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    await refreshProfile(data.session);
    return data;
  }, [refreshProfile]);

  const logout = useCallback(async () => {
    if (supabase) {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    }
    setSession(null);
    setUser(null);
    setProfile(null);
    setCurrentUser(null);
    setLoading(false);
  }, []);

  const updateProfile = useCallback(async (updates) => {
    if (!supabase || !user) {
      throw new Error('No active Supabase session');
    }

    const payload = {
      display_name: updates.displayName ?? profile?.displayName ?? profile?.display_name,
      photo_url: updates.photoURL ?? profile?.photoURL ?? profile?.photo_url,
      first_name: updates.firstName ?? profile?.firstName ?? profile?.first_name,
      last_name: updates.lastName ?? profile?.lastName ?? profile?.last_name,
      date_of_birth: updates.dateOfBirth ?? profile?.dateOfBirth ?? profile?.date_of_birth,
      gender: updates.gender ?? profile?.gender,
      country: updates.country ?? profile?.country,
      phone: updates.phone ?? profile?.phone
    };

    const { data, error } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', user.id)
      .select('*')
      .single();

    if (error) throw error;

    const nextProfile = normalizeProfile(data, user);
    persistSessionState(session, nextProfile);
    return nextProfile;
  }, [persistSessionState, profile, session, user]);

  const updateEmail = useCallback(async (newEmail) => {
    if (!supabase) throw new Error('Supabase is not configured');
    const { data, error } = await supabase.auth.updateUser({ email: newEmail });
    if (error) throw error;
    await refreshProfile(data.session || session);
    return data;
  }, [refreshProfile, session]);

  const updatePassword = useCallback(async (newPassword) => {
    if (!supabase) throw new Error('Supabase is not configured');
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  }, []);

  const value = {
    user,
    profile,
    session,
    currentUser,
    loading,
    authError,
    isAuthenticated: Boolean(session?.user),
    signup: register,
    register,
    login,
    logout,
    refreshProfile,
    updateProfile,
    updateEmail,
    updatePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

